import React, { useMemo } from "react"
import { Space } from "@mimber/ui-web"
import { SelectField } from "@mimber/forms"
import {
  useMembershipTiers,
  useGroups,
  useRoles,
  useMembers,
} from "@mimber/client/mimber-organization"
import type { AudienceTargetedSelectsProps } from "./AudienceTargetedSelects.types"
import { filterMemberOption } from "./AudienceSelector.utils"

export const AudienceTargetedSelects: React.FunctionComponent<
  AudienceTargetedSelectsProps
> = ({ disabled }) => {
  const { data: tiersData, isLoading: tiersLoading } = useMembershipTiers()
  const { data: groupsData, isLoading: groupsLoading } = useGroups()
  const { data: rolesData, isLoading: rolesLoading } = useRoles()
  const { data: membersData, isLoading: membersLoading } = useMembers()

  const tierOptions = useMemo(
    () =>
      (tiersData?.data ?? []).map((t) => ({
        label: t.name,
        value: t.uuid,
      })),
    [tiersData]
  )

  const groupOptions = useMemo(
    () =>
      (groupsData?.data ?? []).map((g) => ({
        label: g.name,
        value: g.uuid,
      })),
    [groupsData]
  )

  const roleOptions = useMemo(
    () =>
      (rolesData?.data ?? []).map((r) => ({
        label: r.name,
        value: r.uuid,
      })),
    [rolesData]
  )

  const memberOptions = useMemo(
    () =>
      (membersData?.data ?? []).map((m) => ({
        label:
          `${m.user.firstName ?? ""} ${m.user.lastName ?? ""}`.trim() ||
          m.user.email,
        value: m.uuid,
      })),
    [membersData]
  )

  return (
    <Space direction="vertical" style={{ width: "100%" }}>
      <SelectField
        name="tierTargetIds"
        label="Membership Tiers"
        options={tierOptions}
        mode="multiple"
        placeholder="Select membership tiers"
        loading={tiersLoading}
        disabled={disabled}
        allowClear
      />
      <SelectField
        name="groupTargetIds"
        label="Groups"
        options={groupOptions}
        mode="multiple"
        placeholder="Select groups"
        loading={groupsLoading}
        disabled={disabled}
        allowClear
      />
      <SelectField
        name="roleTargetIds"
        label="Organization Roles"
        options={roleOptions}
        mode="multiple"
        placeholder="Select organization roles"
        loading={rolesLoading}
        disabled={disabled}
        allowClear
      />
      <SelectField
        name="memberTargetIds"
        label="Individual Members"
        options={memberOptions}
        mode="multiple"
        placeholder="Search for members"
        loading={membersLoading}
        disabled={disabled}
        allowClear
        showSearch
        filterOption={filterMemberOption}
      />
    </Space>
  )
}
